/**
 * PeopleGraph v1.0 — Main Application
 *
 * Manages authentication, state, and view routing.
 * All view components are imported from src/components/.
 */

import { useState, useEffect } from "react";
import { api } from "./utils/api";
import styles from "./styles/theme";

// Components
import AuthScreen from "./components/AuthScreen";
import DashboardView from "./components/DashboardView";
import TreeView from "./components/TreeView";
import PeopleListView from "./components/PeopleListView";
import PersonFormModal from "./components/PersonFormModal";
import BulkAddModal from "./components/BulkAddModal";
import PersonDetailModal from "./components/PersonDetailModal";
import ProfileView from "./components/ProfileView";
import BackupView from "./components/BackupView";

export default function App() {
  // ── Auth State ──
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authScreen, setAuthScreen] = useState("login");

  // ── App State ──
  const [people, setPeople] = useState([]);
  const [view, setView] = useState("dashboard");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [profilePerson, setProfilePerson] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  // ── Check session on mount ──
  useEffect(() => {
    (async () => {
      try {
        const { user: u } = await api.getMe();
        setUser(u);
        const data = await api.getPeople();
        setPeople(data);
      } catch {
        // Not logged in
      }
      setAuthLoading(false);
    })();
  }, []);

  // ── Listen for auth expiry ──
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setPeople([]);
      setView("dashboard");
    };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);

  // ── Refresh people from server ──
  const refreshPeople = async () => {
    try {
      const data = await api.getPeople();
      setPeople(data);
    } catch {
      // ignore
    }
  };

  // ── Auth handlers ──
  const handleRegister = async (email, password, name, inviteCode) => {
    try {
      const { user: u } = await api.register(email, password, name, inviteCode);
      setUser(u);
      return null;
    } catch (err) {
      return err.message;
    }
  };

  const handleLogin = async (email, password) => {
    try {
      const { user: u } = await api.login(email, password);
      setUser(u);
      const data = await api.getPeople();
      setPeople(data);
      return null;
    } catch (err) {
      return err.message;
    }
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    setUser(null);
    setPeople([]);
    setView("dashboard");
  };

  // ── CRUD handlers ──
  const addPerson = async (personData) => {
    try {
      const newPerson = await api.addPerson(personData);
      setPeople((prev) => [...prev, newPerson]);
      setShowAddForm(false);
      return newPerson;
    } catch (err) {
      console.error("Add person failed:", err);
      return null;
    }
  };

  const bulkAddPeople = async (entries) => {
    try {
      const created = await api.bulkAddPeople(entries);
      setPeople((prev) => [...prev, ...created]);
      setShowBulkAdd(false);
      return created;
    } catch (err) {
      console.error("Bulk add failed:", err);
      return [];
    }
  };

  const updatePerson = async (id, updates) => {
    try {
      const updated = await api.updatePerson(id, updates);
      setPeople((prev) => prev.map((p) => (p.id === id ? updated : p)));
      setEditingPerson(null);
      if (selectedPerson?.id === id) setSelectedPerson(updated);
      if (profilePerson?.id === id) setProfilePerson(updated);
    } catch (err) {
      console.error("Update person failed:", err);
    }
  };

  const deletePerson = async (id) => {
    try {
      await api.deletePerson(id);
      setPeople((prev) =>
        prev
          .filter((p) => p.id !== id)
          .map((p) => (p.parentId === id ? { ...p, parentId: null } : p)),
      );
      setSelectedPerson(null);
      if (profilePerson?.id === id) {
        setProfilePerson(null);
        setView("people");
      }
    } catch (err) {
      console.error("Delete person failed:", err);
    }
  };

  const navigateToProfile = (person) => {
    setProfilePerson(person);
    setView("profile");
  };

  // ── Loading screen ──
  if (authLoading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.loadingLogo}>
          <span style={styles.logoIcon}>◉</span>
          <span style={styles.logoText}>PeopleGraph</span>
        </div>
        <div style={styles.loadingSpinner} />
      </div>
    );
  }

  // ── Auth screens ──
  if (!user) {
    return (
      <AuthScreen
        screen={authScreen}
        setScreen={setAuthScreen}
        onLogin={handleLogin}
        onRegister={handleRegister}
      />
    );
  }

  // ── Main app ──
  return (
    <div style={styles.appContainer}>
      {/* Sidebar */}
      <aside
        style={{
          ...styles.sidebar,
          ...(sidebarOpen ? {} : styles.sidebarCollapsed),
        }}
      >
        <div style={styles.sidebarHeader}>
          <span style={styles.logoIcon}>◉</span>
          {sidebarOpen && (
            <span style={styles.sidebarLogoText}>PeopleGraph</span>
          )}
        </div>

        <nav style={styles.nav}>
          {[
            { id: "dashboard", icon: "📊", label: "Dashboard" },
            { id: "tree", icon: "🌳", label: "Tree View" },
            { id: "people", icon: "👥", label: "All People" },
            { id: "backup", icon: "💾", label: "Backup" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setView(item.id)}
              style={{
                ...styles.navItem,
                ...(view === item.id ? styles.navItemActive : {}),
              }}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        <div style={styles.sidebarFooter}>
          {sidebarOpen && (
            <div style={styles.userInfo}>
              <div style={styles.userAvatar}>
                {user.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div style={styles.userDetails}>
                <div style={styles.userName}>{user.name}</div>
                <div style={styles.userEmail}>{user.email}</div>
              </div>
            </div>
          )}
          <button onClick={handleLogout} style={styles.logoutBtn}>
            {sidebarOpen ? "Sign Out" : "↪"}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={styles.mainContent}>
        {/* Top Bar */}
        <header style={styles.topBar}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={styles.menuBtn}
          >
            ☰
          </button>
          <h1 style={styles.pageTitle}>
            {view === "dashboard" && "Dashboard"}
            {view === "tree" && "Relationship Tree"}
            {view === "people" && "All People"}
            {view === "backup" && "Backup & Restore"}
            {view === "profile" && (
              <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button
                  onClick={() => setView("people")}
                  style={styles.backBtn}
                >
                  ← Back
                </button>
                <span>{profilePerson?.name || "Profile"}</span>
              </span>
            )}
          </h1>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setShowBulkAdd(true)}
              style={styles.bulkAddBtn}
            >
              ⚡ Bulk Add
            </button>
            <button
              onClick={() => {
                setEditingPerson(null);
                setShowAddForm(true);
              }}
              style={styles.addBtn}
            >
              + Add Person
            </button>
          </div>
        </header>

        {/* Content */}
        <div style={styles.content}>
          {view === "dashboard" && (
            <DashboardView
              people={people}
              onViewChange={setView}
              onSelectPerson={navigateToProfile}
            />
          )}
          {view === "tree" && (
            <TreeView people={people} onSelectPerson={navigateToProfile} />
          )}
          {view === "people" && (
            <PeopleListView
              people={people}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filterCategory={filterCategory}
              setFilterCategory={setFilterCategory}
              onSelect={navigateToProfile}
              onEdit={(p) => {
                setEditingPerson(p);
                setShowAddForm(true);
              }}
              onDelete={deletePerson}
            />
          )}
          {view === "backup" && (
            <BackupView people={people} user={user} onRefresh={refreshPeople} />
          )}
          {view === "profile" && profilePerson && (
            <ProfileView
              person={
                people.find((p) => p.id === profilePerson.id) || profilePerson
              }
              people={people}
              onEdit={(p) => {
                setEditingPerson(p);
                setShowAddForm(true);
              }}
              onDelete={(id) => {
                deletePerson(id);
                setView("people");
              }}
              onSelectPerson={navigateToProfile}
              onBack={() => setView("people")}
            />
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      {showAddForm && (
        <PersonFormModal
          person={editingPerson}
          people={people}
          onSave={
            editingPerson
              ? (data) => updatePerson(editingPerson.id, data)
              : addPerson
          }
          onClose={() => {
            setShowAddForm(false);
            setEditingPerson(null);
          }}
        />
      )}

      {/* Bulk Add Modal */}
      {showBulkAdd && (
        <BulkAddModal
          people={people}
          onSave={bulkAddPeople}
          onClose={() => setShowBulkAdd(false)}
        />
      )}

      {/* Person Detail Modal */}
      {selectedPerson && !showAddForm && (
        <PersonDetailModal
          person={selectedPerson}
          people={people}
          onClose={() => setSelectedPerson(null)}
          onEdit={(p) => {
            setEditingPerson(p);
            setShowAddForm(true);
          }}
          onDelete={deletePerson}
        />
      )}
    </div>
  );
}
